import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, concat, Subject } from 'rxjs';
import { buffer, filter, connect, take } from 'rxjs/operators';
import { IIntercomOptions } from '../interfaces/intercom-options.interface';

import { IIntercomConfig } from '../interfaces/intercom-config.interface';
import { INTERCOM_GONFIG_TOKEN } from '../tokens';

interface IIntercomCommand {
  fn: string;
  args?: IIntercomOptions;
  eventName?: string;
}

type IIntercom = {
  Intercom: (a: unknown, b: unknown, c?: unknown) => void;
};
@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  private initialized = false;
  private booted$ = new BehaviorSubject(false);
  private renderer: Renderer2;
  private intercomLoaded = new Subject<void>();
  private intercomCommands = new Subject<IIntercomCommand>();

  public isBooted$ = this.booted$.asObservable();

  constructor(
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(INTERCOM_GONFIG_TOKEN) private settings: IIntercomConfig,
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.initialize();
  }

  private get getHasAppId(): boolean {
    return Boolean(this.settings) && Boolean(this.settings.appId);
  }

  private initialize() {
    if (this.initialized || !this.getHasAppId) {
      return;
    }

    const headScript = this.renderer.createElement('script');
    headScript.type = 'text/javascript';
    headScript.async = true;
    headScript.src = `https://widget.intercom.io/widget/${this.settings.appId}`;
    headScript.onload = () => {
      this.intercomLoaded.next();
    };

    this.renderer.appendChild(this.document.head, headScript);
    this.listenIntercomCommands();

    this.initialized = true;
  }

  public boot(options?: IIntercomOptions) {
    if (!this.getHasAppId || this.booted$.value) {
      return;
    }

    this.intercomCommands.next({
      fn: 'boot',
      args: {
        ...options,
        alignment: 'right',
      },
    });

    this.booted$.next(true);
  }

  public shutdown() {
    this.intercomCommands.next({
      fn: 'shutdown',
    });
    this.booted$.next(false);
  }

  public update(options: IIntercomOptions) {
    this.whenBooted(() => {
      this.intercomCommands.next({
        fn: 'update',
        args: options,
      });
    });
  }

  public trackEvent(
    eventName: string,
    params: Omit<IIntercomOptions, 'app_id'>,
  ) {
    this.whenBooted(() => {
      this.intercomCommands.next({
        fn: 'trackEvent',
        eventName,
        args: params,
      });
    });
  }

  private listenIntercomCommands() {
    this.intercomCommands
      .pipe(
        connect(
          (stream) => {
            return concat(
              stream.pipe(buffer(this.intercomLoaded), take(1)),
              stream,
            );
          },
          { connector: () => new Subject() },
        ),
      )
      .subscribe((data: IIntercomCommand | IIntercomCommand[]) => {
        if (Array.isArray(data)) {
          data.forEach((command) => {
            this.callIntercom(command.fn, command.args, command.eventName);
          });

          return;
        }

        this.callIntercom(data.fn, data.args, data.eventName);
      });
  }

  private callIntercom(
    fn: string,
    args?: IIntercomOptions,
    eventName?: string,
  ) {
    if (!this.hasIntercom(window)) {
      return;
    }

    if (fn === 'boot') {
      args = {
        ...args,
        app_id: args?.app_id ?? this.settings.appId,
      };
    }

    if (eventName) {
      window.Intercom(fn, eventName, args);

      return;
    }

    window.Intercom(fn, args);
  }

  public whenBooted(action: () => void): void {
    this.booted$.pipe(filter((v) => v)).subscribe(() => {
      action();
    });
  }

  public hasIntercom(window: Window): window is Window & IIntercom {
    const fnName: keyof IIntercom = 'Intercom';
    return Object.keys(window).indexOf(fnName) !== -1;
  }
}
